import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  CheckCircle, CreditCard, Shield, Clock, 
  Loader2, ArrowLeft, Sparkles, Tag
} from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

// Pricing configuration - Solo per service_provider (candidati e aziende sono gratis)
const PRICING = {
  service_provider: {
    price: 24,
    label: "Fornitore Servizi",
    color: "#10B981",
    features: [
      "Annunci servizi illimitati",
      "Visibilità a tutti i clienti",
      "Contatti diretti via WhatsApp",
      "Portfolio e foto",
      "Supporto dedicato"
    ]
  }
};

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingPaypal, setLoadingPaypal] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // stripe or paypal

  // Candidati sono gratuiti - redirect alla dashboard
  useEffect(() => {
    if (user?.user_type === "candidate") {
      toast.info("L'account candidato è gratuito!");
      navigate("/dashboard/candidate");
    }
  }, [user, navigate]);

  // Get pricing based on user type
  const pricing = PRICING[user?.user_type] || PRICING.service_provider;

  const handleStripeSubscribe = async () => {
    if (user?.subscription_active) {
      toast.info("Hai già un abbonamento attivo");
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        package_type: "annual",
        user_type: user?.user_type,
        origin_url: originUrl,
        promo_code: promoCode || null
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Errore nella creazione del pagamento");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante il pagamento";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalSubscribe = async () => {
    if (user?.subscription_active) {
      toast.info("Hai già un abbonamento attivo");
      return;
    }

    setLoadingPaypal(true);
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        promo_code: promoCode || null
      });

      if (response.data.status === "free") {
        toast.success(response.data.message);
        await refreshUser();
        navigate("/dashboard/provider");
        return;
      }

      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error("Errore nella creazione ordine PayPal");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore PayPal";
      toast.error(message);
    } finally {
      setLoadingPaypal(false);
    }
  };

  const getDashboardLink = () => {
    if (user?.user_type === "candidate") return "/dashboard/candidate";
    if (user?.user_type === "service_provider") return "/dashboard/provider";
    return "/";
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="subscription-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to={getDashboardLink()}
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alla dashboard
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#DA291C]/10 text-[#DA291C] text-sm font-semibold rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            Usa il codice LANCIO50 per -50%
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Abbonamento {pricing.label}
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Un piccolo investimento per accedere a tutte le opportunità nel Canton Ticino
          </p>
        </div>

        {/* Current Status */}
        {user?.subscription_active && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8 text-center" data-testid="active-subscription-banner">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-emerald-800 mb-2">
              Abbonamento attivo
            </h2>
            <p className="text-emerald-700">
              Il tuo abbonamento è attivo. Puoi candidarti a tutti gli annunci!
            </p>
            {user.subscription_expires && (
              <p className="text-sm text-emerald-600 mt-2">
                Scade il {new Date(user.subscription_expires).toLocaleDateString("it-CH")}
              </p>
            )}
          </div>
        )}

        {/* Pricing Card */}
        {!user?.subscription_active && (
          <div className="max-w-md mx-auto">
            <div 
              className="bg-white rounded-2xl border-2 shadow-lg p-8 text-center" 
              style={{ borderColor: pricing.color, boxShadow: `0 10px 15px -3px ${pricing.color}20` }}
              data-testid="pricing-card"
            >
              {/* Badge */}
              <div 
                className="inline-block px-3 py-1 text-white text-xs font-bold rounded-full mb-6"
                style={{ backgroundColor: pricing.color }}
              >
                {pricing.label.toUpperCase()}
              </div>

              {/* Price */}
              <div className="mb-8">
                {/* Annual Badge - Primary Focus */}
                <div className="bg-emerald-700 text-white text-lg sm:text-xl font-bold px-6 py-3 rounded-xl mb-4 inline-block">
                  ABBONAMENTO ANNUALE
                </div>
                
                {/* Price - Secondary */}
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-3xl font-semibold text-slate-600">{pricing.price}</span>
                  <span className="text-xl text-slate-400">CHF</span>
                </div>
                
                {/* Duration Highlight */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg py-3 px-4 mt-4">
                  <p className="text-emerald-800 font-bold text-lg">Validità 365 giorni</p>
                  <p className="text-emerald-600 text-sm">Meno di {(pricing.price / 12).toFixed(0)} CHF al mese</p>
                </div>
                
                {/* Promo Code */}
                <p className="text-sm text-amber-600 mt-4 font-medium bg-amber-50 py-2 px-3 rounded-lg inline-block">
                  🎁 Usa codice LANCIO50 per -50%
                </p>
                
                {/* Promo Code Input */}
                <div className="mt-6 text-left">
                  <Label htmlFor="promo-code" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Codice Sconto (opzionale)
                  </Label>
                  <Input
                    id="promo-code"
                    type="text"
                    placeholder="Inserisci codice sconto"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400"
                  />
                  <p className="text-xs text-slate-500 mt-1">Il codice verrà applicato automaticamente</p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-4 text-left mb-8">
                {pricing.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Buttons - Choose Payment Method */}
              <div className="space-y-3">
                {/* Stripe Button */}
                <Button
                  onClick={handleStripeSubscribe}
                  disabled={loading || loadingPaypal}
                  className="w-full h-14 text-lg font-bold text-white bg-[#635BFF] hover:bg-[#5851DB]"
                  data-testid="subscribe-stripe-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Reindirizzamento...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Paga con Carta / TWINT
                    </>
                  )}
                </Button>

                <div className="text-center text-sm text-slate-400">oppure</div>

                {/* PayPal Button */}
                <Button
                  onClick={handlePayPalSubscribe}
                  disabled={loading || loadingPaypal}
                  className="w-full h-14 text-lg font-bold text-white bg-[#0070BA] hover:bg-[#005EA6]"
                  data-testid="subscribe-paypal-button"
                >
                  {loadingPaypal ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Reindirizzamento a PayPal...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.67.67 0 0 1 .66-.557h6.47c2.08 0 3.657.503 4.687 1.497 1.03.994 1.363 2.439 1.001 4.298-.1.544-.26 1.07-.481 1.578-.221.509-.5.989-.836 1.44-.336.451-.72.858-1.152 1.22-.432.362-.9.664-1.405.906-.505.242-1.04.416-1.605.523-.565.107-1.147.16-1.745.16H7.972l-.896 6.552z"/>
                      </svg>
                      Paga con PayPal
                    </>
                  )}
                </Button>
              </div>

              {/* Payment Methods */}
              <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Shield className="w-4 h-4" />
                  Pagamento sicuro
                </div>
                <div className="text-sm text-slate-400">•</div>
                <div className="text-sm text-slate-500">
                  Stripe & PayPal
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ / Info */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-[#004E64]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-6 h-6 text-[#004E64]" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Pagamento sicuro</h3>
            <p className="text-sm text-slate-600">
              Pagamenti gestiti da Stripe e PayPal, con supporto carte, TWINT e conto PayPal
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-[#004E64]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-[#004E64]" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Attivazione immediata</h3>
            <p className="text-sm text-slate-600">
              Accesso immediato dopo il pagamento, candidati subito
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-[#004E64]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-[#004E64]" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Dati protetti</h3>
            <p className="text-sm text-slate-600">
              I tuoi dati sono al sicuro e non vengono condivisi
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
